import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import {
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  TagIcon,
  MapPinIcon,
  BanknotesIcon,
  LanguageIcon,
  SpeakerWaveIcon,
  PlayIcon,
  PauseIcon,
  ChevronDownIcon,
  CheckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

/**
 * Step 1 of the Qualifying Agent flow: who is calling, about what, and on which channel.
 *
 * This replaces two separate steps. The old step 1 was a full screen asking one question —
 * which channel — that already had a default selected, so most recruiters clicked Next without
 * reading it, and everybody paid a page transition for it. It is a two-state choice with an
 * obvious default, which is what a segmented control is for. Folding it in also removes the
 * step where a recruiter had to commit to a channel before seeing what the agent would say.
 *
 * Everything on this screen is either spoken to the candidate or shown to them, except the
 * screening name. The hints say which is which, because a recruiter filling "Organisation" has
 * no other way to know the value gets read out loud on a phone call.
 */

const CHANNELS = [
  { value: 'call', label: 'Call only', Icon: PhoneIcon },
  { value: 'both', label: 'Call + WhatsApp', Icon: PhoneIcon },
  { value: 'whatsapp', label: 'WhatsApp only', Icon: ChatBubbleLeftRightIcon },
];

const CHANNEL_HELP = {
  call: 'Phone calls only. Missed calls are tried again later.',
  both: 'If one doesn’t reach the candidate, the other picks up where they left off.',
  whatsapp: 'WhatsApp messages only. No calls.',
};

// The languages the call can be spoken in. Same ids as the AI Interview's LANGUAGE_OPTIONS
// (pages/ai/AIInterviewCreate.js) and server utils/languageCodes.js — copied, not imported, per
// the qualifying agent's isolation rule; the server rejects any id not in its own list.
// Voice names mirror server/modules/qualifying-agent/languages.js — the recruiter picks a gender,
// the server resolves it to that voice. Hinglish speaks with the Hindi voices.
const CALL_LANGUAGES = [
  { id: 'en', name: 'English', native: 'English', voices: { female: 'Kavita', male: 'Devansh' } },
  { id: 'hi', name: 'Hindi', native: 'हिन्दी', voices: { female: 'Riya', male: 'Rohan' } },
  { id: 'hi-en', name: 'Hinglish', native: 'Hindi + English', voices: { female: 'Riya', male: 'Rohan' } },
  { id: 'mr', name: 'Marathi', native: 'मराठी', voices: { female: 'Anika', male: 'Suresh' } },
  { id: 'gu', name: 'Gujarati', native: 'ગુજરાતી', voices: { female: 'Isha', male: 'Amit' } },
  { id: 'bn', name: 'Bengali', native: 'বাংলা', voices: { female: 'Ananya', male: 'Rubel' } },
  { id: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ', voices: { female: 'Jaspreet', male: 'Gurpreet' } },
  { id: 'ta', name: 'Tamil', native: 'தமிழ்', voices: { female: 'Lakshmi', male: 'Arun' } },
  { id: 'te', name: 'Telugu', native: 'తెలుగు', voices: { female: 'Sindhu', male: 'Pavan' } },
  { id: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ', voices: { female: 'Divya', male: 'Prakash' } },
  { id: 'ml', name: 'Malayalam', native: 'മലയാളം', voices: { female: 'Latha', male: 'Vijay' } },
];

const VOICE_GENDERS = [
  { value: 'female', label: 'Female voice' },
  { value: 'male', label: 'Male voice' },
];

// Samples are the call's own voice, in the call's language, at 8 kHz phone quality — what the
// candidate will actually hear. Made by server/modules/qualifying-agent/scripts/generateVoicePreviews.js.
const previewUrl = (language, gender) => `/qualifying-agent-voices/${language}-${gender}.wav`;

export default function QualifyingAgentSetupStep({ value, channel, onChange, onChannelChange }) {
  const set = (key, v) => onChange({ ...value, [key]: v });

  return (
    <div className="space-y-5">
      {/* ── How the agent reaches the candidate ─────────────────────────────── */}
      <Field label="How the agent reaches candidates" required Icon={PhoneIcon}>
        <div className="inline-flex w-full rounded-xl border border-gray-300 bg-gray-50 p-1">
          {CHANNELS.map(({ value: v, label, Icon }) => {
            const selected = channel.mode === v;
            return (
              <button
                key={v}
                type="button"
                data-testid={`qa-channel-${v}`}
                onClick={() => onChannelChange({ ...channel, mode: v })}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                  selected
                    ? 'bg-white text-[#007BFF] shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11px] text-gray-500">{CHANNEL_HELP[channel.mode]}</p>
      </Field>

      {/* Language and voice only matter when there is a call: the WhatsApp leg is always English. */}
      {channel.mode !== 'whatsapp' && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field
            label="Call language"
            Icon={LanguageIcon}
            hint={
              value.language && value.language !== 'en'
                ? 'Write questions in English. The agent speaks them in this language.'
                : 'The language the agent speaks. WhatsApp is always in English.'
            }
          >
            <LanguagePicker value={value.language || 'en'} onChange={(v) => set('language', v)} />
          </Field>

          <Field
            label="Agent voice"
            Icon={SpeakerWaveIcon}
            hint="Tap play to hear how it sounds on a call."
          >
            <VoicePicker
              language={value.language || 'en'}
              gender={value.voiceGender || 'female'}
              onChange={(v) => set('voiceGender', v)}
            />
          </Field>
        </div>
      )}

      {channel.mode !== 'whatsapp' && (
        <CallHoursField
          start={value.callWindowStart || DEFAULT_CALL_HOURS.start}
          end={value.callWindowEnd || DEFAULT_CALL_HOURS.end}
          timeZone={value.callTimeZone || DEFAULT_TIME_ZONE}
          onChange={(start, end) => onChange({ ...value, callWindowStart: start, callWindowEnd: end })}
          onTimeZoneChange={(tz) => {
            // Hours that are not allowed in the new zone snap to its widest legal window.
            const fits = !callHoursError(value.callWindowStart, value.callWindowEnd, tz);
            const b = callWindowBounds(tz);
            onChange({
              ...value,
              callTimeZone: tz,
              ...(fits ? {} : { callWindowStart: b.start, callWindowEnd: b.end }),
            });
          }}
        />
      )}

      <hr className="border-gray-100" />

      <Field label="Screening name" required Icon={TagIcon} hint="Only your team sees this.">
        <input
          type="text"
          value={value.screeningName}
          onChange={(e) => set('screeningName', e.target.value)}
          placeholder="e.g. Qualifying — Customer Success Manager"
          data-testid="qualifying-agent-screening-name"
          className={INPUT}
        />
      </Field>

      <Field
        label="Company name"
        required
        Icon={BuildingOfficeIcon}
        hint="Said on the call. Use the name candidates know."
      >
        <input
          type="text"
          value={value.orgName}
          onChange={(e) => set('orgName', e.target.value)}
          placeholder="e.g. Indara Tech"
          data-testid="qualifying-agent-org-name"
          className={INPUT}
        />
      </Field>

      <Field label="Role" required Icon={BriefcaseIcon}>
        <div className="rounded-xl border border-gray-200 p-3">
          <input
            type="text"
            value={value.role}
            onChange={(e) => set('role', e.target.value)}
            placeholder="e.g. Customer Success Manager"
            className={INPUT}
          />
          <TellCandidates
            checked={value.showRole !== false}
            onChange={(v) => set('showRole', v)}
            testId="qa-show-role"
            on="the agent names the role in the intro."
            off="the agent says “a job opening” instead."
          />
        </div>
      </Field>

      <Field label="Location" Icon={MapPinIcon}>
        <div className="rounded-xl border border-gray-200 p-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={value.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="e.g. Mumbai, India"
              className={`${INPUT} min-w-0 flex-1`}
            />
            {/* Stored as onsite / remote / hybrid (the job post's values); said on the call as
                "in-office", "work from home", "hybrid" (server roleBriefWriter.js). */}
            <select
              value={value.locationType || 'onsite'}
              onChange={(e) => set('locationType', e.target.value)}
              aria-label="Work arrangement"
              data-testid="qa-location-type"
              className="shrink-0 rounded-xl border border-gray-300 bg-white px-2 py-2.5 text-sm text-gray-700 focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/40"
            >
              <option value="onsite">In-office</option>
              <option value="remote">Work from home</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          <TellCandidates
            checked={value.showLocation !== false}
            onChange={(v) => set('showLocation', v)}
            testId="qa-show-location"
            on="the agent says the city in the role brief, and answers if asked."
            off="the agent won’t share the city."
          />
          <TellCandidates
            checked={value.showLocType !== false}
            onChange={(v) => set('showLocType', v)}
            testId="qa-show-location-type"
            on={`the agent says it is ${WORK_TYPE_LABEL[value.locationType || 'onsite'].toLowerCase()} in the role brief.`}
            off="the agent won’t say how the role is worked."
          />
        </div>
      </Field>

      {/* Off unless the recruiter turns it on: the range is what a salary answer is scored
          against, and what the agent shares if asked. */}
      <Field label="Salary range" Icon={BanknotesIcon}>
        <div className="rounded-xl border border-gray-200 p-3">
          <TellCandidates
            checked={!!value.showSalary}
            onChange={(v) => set('showSalary', v)}
            testId="qa-show-salary"
            on="the agent says the range in the role brief, and checks expected pay against it."
            off="the range isn’t shared or used to score answers."
          />

          {value.showSalary && (
            <div className="mt-3 flex items-center gap-2">
              <select
                value={value.currency}
                onChange={(e) => set('currency', e.target.value)}
                aria-label="Currency"
                className="shrink-0 rounded-xl border border-gray-300 bg-white px-2 py-2.5 text-sm text-gray-700 focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/40"
              >
                {['INR', 'USD', 'EUR', 'GBP', 'AED'].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={value.salaryMin}
                onChange={(e) => set('salaryMin', e.target.value)}
                placeholder="Minimum"
                className={`${INPUT} min-w-0 flex-1`}
              />
              <span className="shrink-0 text-gray-400">–</span>
              <input
                type="number"
                value={value.salaryMax}
                onChange={(e) => set('salaryMax', e.target.value)}
                placeholder="Maximum"
                className={`${INPUT} min-w-0 flex-1`}
              />
            </div>
          )}
        </div>
      </Field>

    </div>
  );
}

// ── internal ─────────────────────────────────────────────────────────────────

// Same look as the custom dropdowns on the candidate apply page (pages/public/ScreeningApply.js).
const LISTBOX_BUTTON =
  'relative flex w-full cursor-pointer items-center gap-2.5 rounded-xl border bg-white py-2 pl-2.5 pr-10 text-left text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#007BFF]/40';
const LISTBOX_OPTIONS =
  'absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-gray-100 bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none';

function Chevron({ open }) {
  return (
    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
      <ChevronDownIcon
        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        aria-hidden="true"
      />
    </span>
  );
}

const buttonState = (open) =>
  open ? 'border-[#007BFF] ring-2 ring-[#007BFF]/25' : 'border-gray-300 hover:border-[#007BFF]/60';

function LanguagePicker({ value, onChange }) {
  const current = CALL_LANGUAGES.find((l) => l.id === value) || CALL_LANGUAGES[0];
  return (
    <Listbox value={current.id} onChange={onChange}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button data-testid="qa-call-language" className={`${LISTBOX_BUTTON} ${buttonState(open)}`}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#007BFF]/10 text-[#007BFF]">
              <LanguageIcon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-semibold text-gray-900">{current.name}</span>
              <span className="block truncate text-[11px] text-gray-500">{current.native}</span>
            </span>
            <Chevron open={open} />
          </Listbox.Button>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options className={LISTBOX_OPTIONS}>
              {CALL_LANGUAGES.map((lang) => (
                <Listbox.Option
                  key={lang.id}
                  value={lang.id}
                  data-testid={`qa-call-language-option-${lang.id}`}
                  className={({ active }) =>
                    `relative flex cursor-pointer select-none items-center justify-between gap-3 py-2.5 pl-4 pr-9 transition-colors ${
                      active ? 'bg-[#007BFF]/5 text-[#007BFF]' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className={`truncate ${selected ? 'font-semibold text-[#007BFF]' : 'font-medium'}`}>{lang.name}</span>
                      {lang.id !== 'en' && <span className="shrink-0 text-xs text-gray-400">{lang.native}</span>}
                      {selected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#007BFF]">
                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}

/**
 * Same interaction as the AI Interview's voice picker (pages/ai/AIInterviewCreate.js): a
 * dropdown whose button and every option carry a play button. Playing never selects, and
 * selecting never plays — a recruiter comparing voices must not change the call's voice by
 * accident.
 */
function VoicePicker({ language, gender, onChange }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(null);
  const lang = CALL_LANGUAGES.find((l) => l.id === language) || CALL_LANGUAGES[0];

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlaying(null);
  };

  // A sample in the old language must not keep playing after the language changes.
  useEffect(() => stop, [language]);

  const toggle = (v) => {
    const wasPlaying = playing === v;
    stop();
    if (wasPlaying) {
      return;
    }
    const audio = new Audio(previewUrl(lang.id, v));
    audioRef.current = audio;
    setPlaying(v);
    const done = () => {
      if (audioRef.current === audio) {
        audioRef.current = null;
        setPlaying(null);
      }
    };
    audio.onended = done;
    audio.onerror = done;
    audio.play().catch(done);
  };

  const playButton = (v, round) => (
    <span
      role="button"
      tabIndex={0}
      data-testid={`qa-voice-preview-${v}`}
      aria-label={playing === v ? `Pause ${lang.voices[v]} sample` : `Play ${lang.voices[v]} sample`}
      title="Preview voice"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        toggle(v);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          toggle(v);
        }
      }}
      className={`flex shrink-0 cursor-pointer items-center justify-center transition-all ${
        round
          ? `h-9 w-9 rounded-full ${
              playing === v
                ? 'bg-gradient-to-br from-[#007BFF] to-[#0056b3] text-white shadow-sm'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`
          : 'h-8 w-8 rounded-lg bg-[#007BFF]/10 text-[#007BFF] hover:bg-[#007BFF]/20'
      }`}
    >
      {playing === v ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
    </span>
  );

  const labelFor = (v) => VOICE_GENDERS.find((g) => g.value === v).label;

  return (
    <Listbox value={gender} onChange={onChange}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button as="div" role="button" tabIndex={0} data-testid="qa-voice" className={`${LISTBOX_BUTTON} ${buttonState(open)}`}>
            {playButton(gender, false)}
            <span className="min-w-0">
              <span className="block truncate font-semibold text-gray-900">
                {lang.voices[gender]}
                <span className="font-medium text-gray-400"> · </span>
                <span className="font-medium text-gray-700">{labelFor(gender)}</span>
              </span>
              <span className="block truncate text-[11px] text-gray-500">{lang.name} · phone quality</span>
            </span>
            <Chevron open={open} />
          </Listbox.Button>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options className={LISTBOX_OPTIONS}>
              {VOICE_GENDERS.map(({ value: v, label }) => (
                <Listbox.Option
                  key={v}
                  value={v}
                  data-testid={`qa-voice-${v}`}
                  className={({ active, selected }) =>
                    `flex cursor-pointer items-center gap-2.5 border-l-[3px] px-3 py-2.5 transition-all ${
                      selected ? 'border-l-[#007BFF] bg-[#007BFF]/5' : `border-l-transparent ${active ? 'bg-gray-50' : ''}`
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      {playButton(v, true)}
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${selected ? 'text-[#007BFF]' : 'text-gray-900'}`}>{lang.voices[v]}</span>
                          <span className="text-gray-300">•</span>
                          <span className={`text-sm font-medium ${selected ? 'text-[#007BFF]' : 'text-gray-700'}`}>{label}</span>
                        </span>
                        <span className="block text-xs text-gray-500">Native {lang.name} voice</span>
                      </span>
                      {selected && <CheckIcon className="h-5 w-5 shrink-0 text-[#007BFF]" />}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}

const INPUT =
  'w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-[#007BFF] focus:outline-none focus:ring-2 focus:ring-[#007BFF]/40';

// Mirrors CALL_WINDOW_BOUNDS / DEFAULT_CALL_HOURS / validateCallWindow in
// server/modules/qualifying-agent/callSchedule.js (the server re-checks; this only explains the
// problem before Save). The default is office hours (28 Oct); the organisation's call hours (super
// admin) still apply on top at invite time.
const CALL_HOURS_BOUNDS = { start: '08:00', end: '22:00' };
export const DEFAULT_CALL_HOURS = { start: '10:00', end: '21:00' };

const WORK_TYPE_LABEL = { onsite: 'In-office', remote: 'Work from home', hybrid: 'Hybrid' };
// The candidate's time zone: call hours are their local clock. Any IANA zone saves (the server
// checks it); this is the short list recruiters actually hire in.
export const DEFAULT_TIME_ZONE = 'Asia/Kolkata';
const TIME_ZONES = [
  { value: 'Asia/Kolkata', label: 'India time (IST)' },
  { value: 'Asia/Manila', label: 'Philippines (PHT)' },
  { value: 'America/New_York', label: 'US Eastern (ET)' },
  { value: 'America/Chicago', label: 'US Central (CT)' },
  { value: 'America/Denver', label: 'US Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (PT)' },
  { value: 'Europe/London', label: 'UK (GMT/BST)' },
  { value: 'Asia/Dubai', label: 'UAE (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Australia Eastern (AET)' },
];
// Legal calling hours per zone — mirrors ZONE_BOUNDS in server callSchedule.js (US: TCPA plus
// the 8 PM states; UAE: 9 AM–6 PM). Everywhere else: 8 AM–10 PM.
const ZONE_BOUNDS = [
  { prefix: 'America/', start: '08:00', end: '20:00' },
  { prefix: 'Asia/Dubai', start: '09:00', end: '18:00' },
  { prefix: 'Europe/London', start: '08:00', end: '21:00' },
  { prefix: 'Asia/Manila', start: '08:00', end: '21:00' },
];
export function callWindowBounds(timeZone) {
  const zone = ZONE_BOUNDS.find((z) => String(timeZone || '').startsWith(z.prefix));
  return zone ? { start: zone.start, end: zone.end } : CALL_HOURS_BOUNDS;
}
// The hours a new screening gets in this zone: a listed zone's legal hours, else office hours.
function defaultCallHoursFor(timeZone) {
  const zone = ZONE_BOUNDS.find((z) => String(timeZone || '').startsWith(z.prefix));
  return zone ? { start: zone.start, end: zone.end } : DEFAULT_CALL_HOURS;
}

const toMinutes = (hm) => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hm || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** The problem with a call window in words, or '' when it is fine. */
export function callHoursError(start, end, timeZone = DEFAULT_TIME_ZONE) {
  const s = toMinutes(start);
  const e = toMinutes(end);
  const bounds = callWindowBounds(timeZone);
  if (s === null || e === null) return 'Enter both times.';
  if (s < toMinutes(bounds.start) || e > toMinutes(bounds.end)) {
    return `In this time zone, calls are allowed only between ${to12h(bounds.start)} and ${to12h(bounds.end)}.`;
  }
  if (e - s < 60) return 'Keep at least one hour between the two times.';
  return '';
}

/** "08:00" → "8:00 AM", for the sentence under the inputs. */
function to12h(hm) {
  const mins = toMinutes(hm);
  if (mins === null) return hm;
  const h = Math.floor(mins / 60);
  return `${h % 12 || 12}:${String(mins % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * Two time inputs, typed or picked, and the candidates' time zone (India by default). The
 * sentence underneath says what the setting does in this screening's own times.
 */
function CallHoursField({ start, end, timeZone, onChange, onTimeZoneChange }) {
  const zones = TIME_ZONES.some((z) => z.value === timeZone)
    ? TIME_ZONES
    : [...TIME_ZONES, { value: timeZone, label: timeZone }];
  const zoneLabel = zones.find((z) => z.value === timeZone).label;
  const bounds = callWindowBounds(timeZone);
  const error = callHoursError(start, end, timeZone);
  const defaults = defaultCallHoursFor(timeZone);
  const isDefault = start === defaults.start && end === defaults.end;
  const timeInput =
    'w-[8.5rem] rounded-xl border px-3 py-2.5 text-sm tabular-nums text-gray-900 transition-all focus:outline-none focus:ring-2';
  const tone = error
    ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
    : 'border-gray-300 focus:border-[#007BFF] focus:ring-[#007BFF]/40';

  return (
    <Field label="Call hours" Icon={ClockIcon}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="time"
          aria-label="Earliest call time"
          data-testid="qa-call-hours-start"
          value={start}
          min={bounds.start}
          max={bounds.end}
          step={900}
          onChange={(e) => onChange(e.target.value, end)}
          className={`${timeInput} ${tone}`}
        />
        <span className="text-sm text-gray-500">to</span>
        <input
          type="time"
          aria-label="Latest call time"
          data-testid="qa-call-hours-end"
          value={end}
          min={bounds.start}
          max={bounds.end}
          step={900}
          onChange={(e) => onChange(start, e.target.value)}
          className={`${timeInput} ${tone}`}
        />
        <Listbox value={timeZone} onChange={onTimeZoneChange}>
          {({ open }) => (
            <div className="relative w-56">
              <Listbox.Button
                data-testid="qa-call-timezone"
                aria-label="Candidates' time zone"
                className={`${LISTBOX_BUTTON} ${buttonState(open)} py-2.5`}
              >
                <span className="block truncate font-medium text-gray-900">{zoneLabel}</span>
                <Chevron open={open} />
              </Listbox.Button>
              <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <Listbox.Options className={LISTBOX_OPTIONS}>
                  {zones.map((z) => (
                    <Listbox.Option
                      key={z.value}
                      value={z.value}
                      data-testid={`qa-call-timezone-option-${z.value}`}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2.5 pl-4 pr-9 transition-colors ${
                          active ? 'bg-[#007BFF]/5 text-[#007BFF]' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-semibold text-[#007BFF]' : 'font-medium'}`}>
                            {z.label}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#007BFF]">
                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          )}
        </Listbox>
        {!isDefault && (
          <button
            type="button"
            onClick={() => onChange(defaults.start, defaults.end)}
            className="text-xs font-medium text-[#007BFF] hover:underline"
          >
            Reset to {to12h(defaults.start)} – {to12h(defaults.end)}
          </button>
        )}
      </div>
      {error ? (
        <p className="mt-1.5 text-[11px] font-medium text-red-600">{error}</p>
      ) : (
        <p className="mt-1.5 text-[11px] text-gray-500">
          Calls go out only between {to12h(start)} and {to12h(end)}, {zoneLabel}. Pick the time zone your
          candidates are in.
        </p>
      )}
    </Field>
  );
}

/** "Tell candidates" switch under a field: on, the value is said on the call; off, kept internal. */
function TellCandidates({ checked, onChange, on, off, testId }) {
  return (
    <label className="mt-1.5 flex cursor-pointer items-start gap-2 first:mt-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]/40"
      />
      <span className="text-[11px] leading-snug text-gray-500">
        <span className="font-semibold text-gray-700">Tell candidates</span> — {checked ? on : off}
      </span>
    </label>
  );
}

function Field({ label, required, Icon, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        {Icon && <Icon className="h-4 w-4 text-gray-400" />}
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}
